import type { QuickLoginRow } from "@observe-tech/utils";
import { useState } from "react";
import { createCognitoAuthApi } from "../../../../shell-handle/auth";
import {
  enrolPin,
  forgetAccount,
  pinLogin,
  shouldOfferPinAfterPasswordLogin,
  type PinLoginOutcome,
} from "../../../../shell-handle/quickLogin";
import { offlineModeEnabled } from "../../../../shell-handle/offlineSession";
import {
  isQuickLoginCapable,
  removeQuickLoginPin,
} from "../../../../shell-handle/quickLoginSession";
import { useOnlineStatus } from "./useOnlineStatus";
import { useSavedAccounts } from "./useSavedAccounts";

export interface QuickLoginController {
  rows: QuickLoginRow[];
  online: boolean;
  probeOnline(): Promise<boolean>;
  refresh(): Promise<QuickLoginRow[]>;
  pinLogin(sub: string, pin: string): Promise<PinLoginOutcome>;
  afterPasswordLogin(): Promise<boolean>;
  enrol(pin: string): Promise<boolean>;
  forget(sub: string): Promise<QuickLoginRow[]>;
  removePin(email: string): Promise<void>;
}

const sameEmail = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

export function useQuickLogin(): QuickLoginController | null | undefined {
  const [capable] = useState(isQuickLoginCapable);
  const { rows, refresh } = useSavedAccounts(capable);
  const { online, probeOnline } = useOnlineStatus(capable && rows != null);

  if (!rows) return rows;

  const auth = createCognitoAuthApi();
  return {
    rows,
    online,
    probeOnline,
    refresh,
    async pinLogin(sub, pin) {
      const deps = { auth, probeOnline, offlineModeEnabled };
      const outcome = await pinLogin(deps, sub, pin);
      void refresh();
      return outcome;
    },
    afterPasswordLogin: () => shouldOfferPinAfterPasswordLogin({ auth }),
    enrol: (pin) => enrolPin({ auth }, pin),
    async forget(sub) {
      await forgetAccount(sub);
      return refresh();
    },
    async removePin(email) {
      const pinned = rows.filter((r) => r.hasPin && sameEmail(r.email, email));
      if (!pinned.length) return;
      await Promise.all(pinned.map((r) => removeQuickLoginPin(r.cognitoSub)));
      await refresh();
    },
  };
}
